import { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  Chip,
  Input,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from '@nextui-org/react';
import { Id, toast } from 'react-toastify';
import { Report, ReportStatus, ReportVisibility } from 'common/src/utils/types';
import { sendEmail } from 'common/src/api/email';
import { PORTAL_URL } from 'common/src/utils/branding';
import { getAllExporters } from 'common/src/api/exporters';
import {
  getReportsWithStats,
  deleteReport,
  publishReport,
  archiveReport,
} from 'common/src/api/reports';
import { format } from 'date-fns';
import { useLocation } from 'wouter';
import Header from '../../../components/layouts/Header';
import { ConfirmModal, PageLayout } from '../../../components/ui';
const SuperadminReports = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [sendingReportId, setSendingReportId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [visibilityFilter, setVisibilityFilter] = useState<string>('all');
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [, setLocation] = useLocation();

  const fetchReports = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (statusFilter !== 'all') filters.status = statusFilter as ReportStatus;
      if (visibilityFilter !== 'all')
        filters.visibility = visibilityFilter as ReportVisibility;
      if (searchTerm) filters.search = searchTerm;

      const data = await getReportsWithStats(filters);
      setReports(data);
    } catch (error) {
      console.error('Error fetching reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [statusFilter, visibilityFilter]);

  const handleSearch = () => {
    fetchReports();
  };

  const handleDelete = async (report: Report) => {
    setSelectedReport(report);
    onOpen();
  };

  const confirmDelete = async () => {
    if (!selectedReport) return;

    const success = await deleteReport(selectedReport.id);
    if (success) {
      toast.success('Report deleted successfully');
      fetchReports();
    } else {
      toast.error('Failed to delete report');
    }
    onClose();
    setSelectedReport(null);
  };

  const handlePublish = async (reportId: number) => {
    const result = await publishReport(reportId);
    if (result) {
      toast.success('Report published successfully');
      fetchReports();
    } else {
      toast.error('Failed to publish report');
    }
  };

  const handleArchive = async (reportId: number) => {
    const result = await archiveReport(reportId);
    if (result) {
      toast.success('Report archived successfully');
      fetchReports();
    } else {
      toast.error('Failed to archive report');
    }
  };

  const handleSendReport = async (report: Report) => {
    let progressToastId: Id | null = null;
    try {
      setSendingReportId(report.id);
      const exporters = await getAllExporters();
      progressToastId = toast.loading('Enviando reporte...');
      let sentCount = 0;

      const subject = `Nuevo reporte publicado: ${report.title}`;
      const html = `
        <p>Estimado/a,</p>
        <p>Está publicado el reporte "${report.title}". Puedes acceder a través del siguiente botón.</p>
      `;
      const link = `${PORTAL_URL}/reports/${report.slug}`;
      const linkText = 'Ver reporte';

      for (const exporter of exporters) {
        await sendEmail({
          exporter_id: exporter.id,
          subject,
          html,
          link,
          linkText,
        });
        sentCount += 1;
        if (progressToastId) {
          toast.update(progressToastId, {
            render: `Enviado a ${sentCount} exportadoras`,
            isLoading: true,
          });
        }
      }

      if (progressToastId) {
        toast.update(progressToastId, {
          render: 'Reporte enviado a todos los usuarios',
          type: 'success',
          isLoading: false,
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error sending report email:', error);
      if (progressToastId) {
        toast.update(progressToastId, {
          render: 'Error al enviar el reporte',
          type: 'error',
          isLoading: false,
          autoClose: 4000,
        });
      } else {
        toast.error('Error al enviar el reporte');
      }
    } finally {
      setSendingReportId(null);
    }
  };

  const handleCopyLink = (slug: string) => {
    const url = `${window.location.origin}/reports/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  const getStatusColor = (
    status: ReportStatus
  ): 'default' | 'primary' | 'success' | 'warning' | 'danger' => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const getVisibilityColor = (
    visibility: ReportVisibility
  ): 'success' | 'warning' => {
    return visibility === 'open' ? 'success' : 'warning';
  };

  return (
    <PageLayout>
      {/* Header */}
      <Header
        title='Newsletter'
        actions={
          <Button
            color='primary'
            onPress={() => setLocation('/superadmin-reports/create')}
          >
            Crear Newsletter
          </Button>
        }
      />

      {/* Filters */}
      <Card className='mb-6'>
        <CardBody>
          <div className='flex flex-wrap gap-4'>
            <Input
              placeholder='Buscar Newsletter...'
              value={searchTerm}
              onValueChange={setSearchTerm}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className='w-full md:w-64'
            />

            <Select
              label='Estado'
              selectedKeys={[statusFilter]}
              onSelectionChange={(keys) =>
                setStatusFilter(Array.from(keys)[0] as string)
              }
              className='w-40'
            >
              <SelectItem key='all'>Todos</SelectItem>
              <SelectItem key='draft'>Borrador</SelectItem>
              <SelectItem key='published'>Publicado</SelectItem>
              <SelectItem key='archived'>Archivado</SelectItem>
            </Select>

            <Select
              label='Visibilidad'
              selectedKeys={[visibilityFilter]}
              onSelectionChange={(keys) =>
                setVisibilityFilter(Array.from(keys)[0] as string)
              }
              className='w-40'
            >
              <SelectItem key='all'>Todos</SelectItem>
              <SelectItem key='open'>Público</SelectItem>
              <SelectItem key='private'>Privado</SelectItem>
            </Select>

            <Button onPress={handleSearch} color='primary'>
              Buscar
            </Button>
          </div>
        </CardBody>
      </Card>

      {/* Reports Table */}
      <Card>
        <CardBody>
          <Table aria-label='Reports table' removeWrapper>
            <TableHeader>
              <TableColumn>TÍTULO</TableColumn>
              <TableColumn>ESTADO</TableColumn>
              <TableColumn>VISIBILIDAD</TableColumn>
              <TableColumn>VISTAS</TableColumn>
              <TableColumn>COMPARTIDOS</TableColumn>
              <TableColumn>APLAUSOS</TableColumn>
              <TableColumn>PUBLICADO</TableColumn>
              <TableColumn>ACCIONES</TableColumn>
            </TableHeader>
            <TableBody
              items={reports}
              isLoading={loading}
              emptyContent='No newsletters found'
            >
              {(report) => (
                <TableRow key={report.id}>
                  <TableCell>
                    <div className='max-w-xs truncate'>{report.title}</div>
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={getStatusColor(report.status)}
                      size='sm'
                      variant='flat'
                    >
                      {report.status}
                    </Chip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      color={getVisibilityColor(report.visibility)}
                      size='sm'
                      variant='flat'
                    >
                      {report.visibility === 'open' ? '🌐 Open' : '🔒 Private'}
                    </Chip>
                  </TableCell>
                  <TableCell>{report.views_count || 0}</TableCell>
                  <TableCell>{report.shares_count || 0}</TableCell>
                  <TableCell>{report.applause_count || 0}</TableCell>
                  <TableCell>
                    {report.published_at
                      ? format(new Date(report.published_at), 'dd/MM/yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <div className='flex gap-2 flex-wrap'>
                      <Button
                        size='sm'
                        variant='flat'
                        onPress={() =>
                          setLocation(`/superadmin-reports/edit/${report.id}`)
                        }
                      >
                        Editar
                      </Button>

                      {(report.status === 'draft' ||
                        report.status === 'archived') && (
                        <Button
                          size='sm'
                          variant='flat'
                          onPress={() => handlePublish(report.id)}
                        >
                          Publicar
                        </Button>
                      )}
                      {report.status !== 'archived' && (
                        <Button
                          size='sm'
                          variant='flat'
                          onPress={() => handleArchive(report.id)}
                        >
                          Archivar
                        </Button>
                      )}

                      {/* <Button
                        size='sm'
                        variant='flat'
                        color='danger'
                        onPress={() => handleDelete(report)}
                      >
                        Eliminar
                      </Button> */}
                      <Button
                        size='sm'
                        variant='flat'
                        color='default'
                        onPress={() =>
                          setLocation(
                            `/superadmin-reports/analytics/${report.id}`
                          )
                        }
                      >
                        Métricas
                      </Button>

                      {report.status === 'published' && (
                        <>
                          <Button
                            size='sm'
                            variant='flat'
                            isLoading={sendingReportId === report.id}
                            isDisabled={sendingReportId === report.id}
                            onPress={() => handleSendReport(report)}
                          >
                            Enviar a todos
                          </Button>
                          <Button
                            size='sm'
                            variant='light'
                            color='primary'
                            onPress={() => handleCopyLink(report.slug)}
                          >
                            Link
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardBody>
      </Card>

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={confirmDelete}
        title='Confirm Delete'
        confirmLabel='Delete'
        cancelLabel='Cancel'
        isDestructive
      >
        Are you sure you want to delete "{selectedReport?.title}"? This
        action cannot be undone.
      </ConfirmModal>
    </PageLayout>
  );
};

export default SuperadminReports;
